// Smart Sash Admin Panel - Device Registration Hook
// Manages session validation + lab selection + gRPC activation flow for device registration
// Route: /device-registration?sessionId=...

import { LabSelectionResult } from '@/components/auth/LabSelection';
import {
  DeviceRegistrationCancelationEvent,
  DeviceRegistrationConfirmationEvent,
} from '@/generated/Common/DeviceRegistrationEvents';
import {
  ActivationFlowConfig,
  ActivationStatus,
  useActivationFlow,
} from '@/hooks/useActivationFlow';
import { deviceRegistrationService } from '@/services/api/deviceRegistrationService';
import { closeSession, sessionValidationService } from '@/services/auth/sessionValidationService';
import { tokenManager } from '@/services/auth/tokenManager';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

export type DeviceRegistrationStage =
  | 'validating'
  | 'session-error'
  | 'form'
  | 'lab-selection'
  | 'activating'
  | 'closed';
export type SessionErrorType = 'missing-params' | 'expired' | 'invalid';

export interface DeviceRegistrationState {
  pageStage: DeviceRegistrationStage;
  sessionErrorType: SessionErrorType | null;
  sessionErrorMessage: string | null;
  sessionId: string | null;
  institutionId: string | null;
  labId: string | null;

  activationStatus: ActivationStatus;
  verificationCode: string | null;
  verificationType: string | null;
  timeRemaining: number;
  activationErrorMessage: string | null;

  login: string | undefined;
  handleFormSuccess: (login: string) => void;
  handleLabSelect: (result: LabSelectionResult) => void;
  handleCancel: () => Promise<void>;
  handleCloseSession: () => Promise<void>;
  handleStartOver: () => void;
  handleGoToLogin: () => void;
}

const DEVICE_REGISTRATION_CONFIG: ActivationFlowConfig = {
  confirmationEvents: ['DeviceRegistrationConfirmationEvent'],
  cancellationEvent: 'DeviceRegistrationCancelationEvent',
  decoders: {
    decodeConfirmation: (data) => DeviceRegistrationConfirmationEvent.decode(data).success,
    decodeCancellation: (data) => {
      DeviceRegistrationCancelationEvent.decode(data);
    },
  },
  requestFn: (params) => deviceRegistrationService.request(params),
  revokeFn: (params) => deviceRegistrationService.finalizePendingRequest(params),
  cancelledMessage: 'Device registration was cancelled',
};

const clearAuthTokens = () => {
  tokenManager.clearTokens();
};

/**
 * Hook for the Device Registration page.
 *
 * After session validation, a login form is shown.
 * After successful login, a lab selection step is shown.
 * Once a lab is selected, activation starts with the lab's institutionId and id.
 */
export const useDeviceRegistration = (): DeviceRegistrationState => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentFlowPath = `${location.pathname}${location.search}`;

  const sessionId = searchParams.get('sessionId');
  const institutionIdFromURL = searchParams.get('institutionId');

  const [pageStage, setPageStage] = useState<DeviceRegistrationStage>('validating');
  const [sessionErrorType, setSessionErrorType] = useState<SessionErrorType | null>(null);
  const [sessionErrorMessage, setSessionErrorMessage] = useState<string | null>(null);
  const [activationEnabled, setActivationEnabled] = useState(false);
  const [institutionId] = useState<string | null>(institutionIdFromURL);
  const [labId, setLabId] = useState<string | null>(null);
  const [login, setLogin] = useState<string | undefined>(undefined);

  // Validate session on mount
  useEffect(() => {
    const validate = async () => {
      if (!sessionId) {
        setSessionErrorType('missing-params');
        setSessionErrorMessage('Missing sessionId parameter');
        setPageStage('session-error');
        return;
      }

      try {
        const response = await sessionValidationService.validateSession(sessionId);

        if (response.isValid) {
          const passwordChangeFlow = tokenManager.getPasswordChangeFlow();
          const shouldResumeAfterPasswordChange =
            passwordChangeFlow?.flowType === 'device-registration' &&
            passwordChangeFlow.redirectPath === currentFlowPath &&
            passwordChangeFlow.nextStep === 'lab-selection' &&
            !tokenManager.requiresPasswordChange();

          if (shouldResumeAfterPasswordChange) {
            setLogin(passwordChangeFlow.login);
            tokenManager.setPasswordChangeFlow(null);
            setPageStage('lab-selection');
            return;
          }

          setPageStage('form');
        } else {
          const errMsg = response.errorMessage || 'Invalid or expired session';
          setSessionErrorMessage(errMsg);
          const lower = errMsg.toLowerCase();
          if (lower.includes('expired') || lower.includes('expire')) {
            setSessionErrorType('expired');
          } else if (lower.includes('parameter') || lower.includes('invalid session')) {
            setSessionErrorType('missing-params');
          } else {
            setSessionErrorType('invalid');
          }
          setPageStage('session-error');
        }
      } catch {
        setSessionErrorMessage('Failed to validate session. Please try again.');
        setSessionErrorType('invalid');
        setPageStage('session-error');
      }
    };

    validate();
  }, [currentFlowPath, sessionId]);

  const activationState = useActivationFlow(
    activationEnabled ? sessionId : null,
    activationEnabled ? institutionId : null,
    activationEnabled ? labId : null,
    DEVICE_REGISTRATION_CONFIG
  );

  // Clear tokens when activation is cancelled or errors out
  useEffect(() => {
    if (activationState.status === 'aborted' || activationState.status === 'cancelled') {
      clearAuthTokens();
    }
  }, [activationState.status]);

  const handleFormSuccess = useCallback((userLogin: string) => {
    setLogin(userLogin);
    tokenManager.setPasswordChangeFlow(null);
    setPageStage('lab-selection');
  }, []);

  const handleLabSelect = useCallback((result: LabSelectionResult) => {
    setLabId(result.labId);
    setActivationEnabled(true);
    setPageStage('activating');
  }, []);

  const handleCancel = useCallback(async () => {
    await activationState.cancel();

    if (sessionId && institutionId && !activationState.sessionClosed) {
      try {
        await closeSession(institutionId, sessionId);
      } catch {
        // Session close failures should not block the aborted state.
      }
    }
  }, [activationState, sessionId, institutionId]);

  const handleStartOver = useCallback(() => {
    clearAuthTokens();
    navigate(`/device-registration?sessionId=${sessionId}`, { replace: true });
  }, [navigate, sessionId]);

  const handleCloseSession = useCallback(async () => {
    if (sessionId && institutionId) {
      try {
        await closeSession(institutionId, sessionId);
      } catch {
        // Still show closed screen even if API call fails
      }
    }
    clearAuthTokens();
    setPageStage('closed');
  }, [sessionId, institutionId]);

  const handleGoToLogin = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  return {
    pageStage,
    sessionErrorType,
    sessionErrorMessage,
    sessionId,
    institutionId,
    labId,
    login,
    activationStatus: activationState.status,
    verificationCode: activationState.verificationCode,
    verificationType: activationState.verificationType,
    timeRemaining: activationState.timeRemaining,
    activationErrorMessage: activationState.errorMessage,
    handleFormSuccess,
    handleLabSelect,
    handleCancel,
    handleCloseSession,
    handleStartOver,
    handleGoToLogin,
  };
};
